import React, { useEffect, useState } from 'react'

import { useRequest } from 'ahooks'
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js'

import ActionButton, {
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import Toast, { Props as ToastProps } from '$extensionSrc/components/Toast'
import { send2faSmsPath } from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'

import FormSubmitButton from './FormSubmitButton'
import FormTextInput from './FormTextInput'
import { authFetch } from '../utils/fetch'

import './Enroll2faSmsForm.scss'

function formatPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) {
    return ''
  }

  // Required; else libphonenumber-js will add the parens back
  // whenever you delete it
  if (phoneNumber.includes('(') && !phoneNumber.includes(')')) {
    return phoneNumber.replace('(', '').slice(0, -1)
  }

  return new AsYouType('US').input(phoneNumber)
}

function isPhoneNumberValid(phoneNumber: string) {
  if (!phoneNumber) {
    return false
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, 'US')

  if (!parsedPhoneNumber) {
    return false
  }

  return parsedPhoneNumber.isValid()
}

export default function Enroll2faSmsForm({
  onClickConfirm,
  onClickSkip,
  onClickSwitchToEnroll2faApp,
  showSkip,
}: {
  onClickConfirm: (phoneNumber: string) => void
  onClickSkip?: () => void
  onClickSwitchToEnroll2faApp: () => void
  showSkip: boolean
}) {
  const [statusToast, setStatusToast] = useState<ToastProps | null>(null)
  const defaultValues = { phone: '' }
  const requiredValidations = {
    phone: (x) => isPhoneNumberValid(x),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  useEffect(() => {
    getFieldChangeHandler('phone')(formatPhoneNumber(values.phone))
  }, [values.phone, getFieldChangeHandler])

  const { run: sendSmsRequest } = useRequest(
    (phone) => {
      const formData = new FormData()
      formData.append('phone', phone)

      return authFetch(
        new Request(send2faSmsPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      throttleWait: 1000 * 5, // 5 seconds
      throttleTrailing: false, // User must resubmit after throttle period
      onBefore: () =>
        setStatusToast({
          text: 'Sending code...',
        }),
      onSuccess: (response: Response) => {
        if (response.status === 200) {
          setStatusToast({
            text: 'Code sent! Redirecting to confirmation page...',
          })
          setTimeout(() => onClickConfirm(values.phone), 500)
        } else {
          setStatusToast({
            text: 'Something went wrong. Please try again later.',
            color: 'red',
            dismissAfterMs: 3000,
          })
        }
      },
    },
  )

  const submitForm = (event) => {
    event.preventDefault()
    sendSmsRequest(values.phone)
  }

  return (
    <form className="Enroll2faSmsForm" onSubmit={submitForm}>
      <div className="enroll-sms-content">
        <div className="form-content">
          <div className="phone-inputs">
            <FormTextInput
              additionalClassNames="country-code-input"
              disabled
              handleChange={() => null}
              isSensitive={false}
              name="countryCode"
              placeholderText="Country code"
              value="+ 1"
            />
            <FormTextInput
              handleChange={(value) => getFieldChangeHandler('phone')(value)}
              isSensitive={false}
              name="phone"
              placeholderText="Phone number"
              value={values.phone}
              requirements={[
                {
                  text: 'Enter your 10-digit number including area code',
                  isFulfilled: getRequiredValidationMetByField('phone'),
                },
              ]}
            />
          </div>
          <InteractiveElement
            className="no-usa-canada-number-link right link2 cursor-pointer"
            onClick={onClickSwitchToEnroll2faApp}
            type="button"
          >
            I don't have a US/Canada phone number
          </InteractiveElement>
        </div>
      </div>
      <div className="buttons">
        <FormSubmitButton isValid={isValid}>Send Code</FormSubmitButton>
        {showSkip && (
          <ActionButton
            onClick={onClickSkip}
            type={ActionButtonType.TERTIARY}
            size={ActionButtonSize.MEDIUM}
          >
            Skip for now
          </ActionButton>
        )}
      </div>
      {statusToast && <Toast {...statusToast} />}
    </form>
  )
}
